import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import balanceUploadDetailServices from '@src/services/balanceUploadDetail.services';
import { getById } from './balanceUploadSlice';
import { ExcelErrorMessageWithGroup, Wait } from '@src/utils/utils';

// Card-narrowing filters; "apply to all" sends the result of these as the card list.
export const hasCardFilter = (filterBody) => {
  const body = filterBody || {};
  return Boolean(
    body.name ||
      body.surname ||
      body.cardNumber ||
      body.referenceNumber ||
      body.refNo ||
      body.deliveryCode ||
      body.addressIdList?.length,
  );
};

export const isDetailFilterActive = (filterBody) =>
  hasCardFilter(filterBody) || Boolean(filterBody?.showOnlyPriced);

export const getLoadBalanceDetailsGetPagedList = createAsyncThunk(
  'getLoadBalanceDetailsGetPagedList',
  async (
    { body, pageNumber, pageSize, isFilter = true, sortParams },
    { getState, dispatch, rejectWithValue, requestId },
  ) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({
          message: 'Lütfen anasayfaya giderek müşteri tipi seçiniz.',
        });
      }
      const { detailTableFilter } = getState()?.balanceUploadDetail || {};
      const filterBody = isFilter
        ? body && { ...detailTableFilter.body, ...body, customerId }
        : body && { ...body, customerId };
      const pageNum = pageNumber === 0 ? 0 : pageNumber || detailTableFilter?.pageNum || 1;
      const pageS = pageSize === 0 ? 0 : pageSize || undefined;
      if (sortParams && Object.keys(sortParams)?.filter((x) => x !== '')?.length === 0) {
        sortParams = {};
      }
      // Persist the filter before the request so that concurrent fetches keep the active filter.
      isFilter && dispatch(setDetailTableFilter({ body: filterBody, pageNum, pageS }));
      const res = await balanceUploadDetailServices.loadBalanceDetailsGetPagedList(
        { ...filterBody, ...sortParams, page: pageNum, pageSize: pageS },
        pageNum,
        pageS,
      );

      const isLatestRequest = getState().balanceUploadDetail.latestDetailsRequestId === requestId;
      if (isFilter && isLatestRequest) {
        dispatch(setBalanceFilterItems(hasCardFilter(filterBody) ? res.data.items || [] : []));
      }
      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getAllLoadBalanceCards = createAsyncThunk(
  'getAllLoadBalanceCards',
  async ({ loadBalanceId, vouId }, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;

      return await balanceUploadDetailServices.getAllLoadBalanceCards({
        customerId,
        loadBalanceId,
        vouId,
      });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getCurrentEmployeeList = createAsyncThunk(
  'getCurrentEmployeeList',
  async (
    { loadBalanceId, showOnlyPriced = null, vouId = null, pageNumber = 1, pageSize = 10 },
    { getState, rejectWithValue },
  ) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;

return await balanceUploadDetailServices.getCurrentEmployeeList({
        customerId,
        loadBalanceId,
        showOnlyPriced,
        vouId,
        pageNumber,
        pageSize,
      });

} catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getLoadBalanceDetail = createAsyncThunk(
  'getLoadBalanceDetail',
  async ({ body, pageNumber, pageSize }, { rejectWithValue }) => {
    try {
      return await balanceUploadDetailServices.loadBalanceDetailsGetPagedDtoList(
        { ...body },
        pageNumber,
        pageSize,
      );
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const loadBalanceDetailPriceUpdate = createAsyncThunk(
  'loadBalanceDetailPriceUpdate',
  async (body, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentLoadBalanceId = await getState()?.balanceUpload?.currentLoadBalance?.id;
      if (!currentLoadBalanceId) {
        return rejectWithValue({
          message: 'Kayıt bulunamadı.',
        });
      }
      try {
        if (
          Number.isNaN(body?.dailyPrice) ||
          body?.dailyPrice === undefined ||
          body?.dailyPrice === null ||
          body?.dailyPrice === 'NaN'
        ) {
          body.dailyPrice = 0;
        }
      } catch (error) {
        console.error(error);
      }
      const res = await balanceUploadDetailServices.updateLoadBalanceDetailPrice(body);
      await Wait(500);
      if (!body.dontReFetch || !res.isSuccess) {
        await dispatch(getById(currentLoadBalanceId));
      }
      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const loadBalanceDetailExcelUpload = createAsyncThunk(
  'loadBalanceDetailExcelUpload',
  async (body, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentLoadBalance = await getState()?.balanceUpload?.currentLoadBalance;
      const currentLoadBalanceId = currentLoadBalance?.id;
      if (!currentLoadBalanceId) {
        return rejectWithValue({
          message: 'LoadBalance Kayıt bulunamadı.',
        });
      }
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({
          message: 'Lütfen anasayfaya giderek müşteri tipi seçiniz.',
        });
      }
      const data = new FormData();
      data.append('FormFile', body?.FormFile, body?.FormFile?.fileName);
      data.append('CustomerId', customerId);
      data.append('VouId', body?.vouId ?? 1);
      data.append('LoadBalanceId', currentLoadBalanceId);
      data.append('ExcelUploadCheck.FirstRowHeaderColumn', body?.FirstRowHeaderColumn);
      data.append('ExcelUploadCheck.UseDefaultTemplate', body?.UseDefaultTemplate);
      data.append('ExcelUploadCheck.PeriodType', body?.PeriodType);
      body?.CardNumberColumn &&
        data.append('ExcelUploadCheck.CardNumberColumn', body?.CardNumberColumn);
      body?.RefNoColumn && data.append('ExcelUploadCheck.RefNoColumn', body?.RefNoColumn);
      body?.DayCountColumn && data.append('ExcelUploadCheck.DayCountColumn', body?.DayCountColumn);
      body?.PriceColumn && data.append('ExcelUploadCheck.PriceColumn', body?.PriceColumn);

      return await balanceUploadDetailServices.loadBalanceDetailExcelUpload(data);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const loadBalanceDetailExcelTemplate = createAsyncThunk(
  'loadBalanceDetailExcelTemplate',
  async (body, { getState, dispatch, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({
          message: 'Lütfen anasayfaya giderek müşteri tipi seçiniz.',
        });
      }

      const res = await balanceUploadDetailServices.loadBalanceDetailExcelTemplate({
        ...body,
        customerId,
      });
      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const loadBalanceDetailUpdate = createAsyncThunk(
  'loadBalanceDetailUpdate',
  async (body, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentLoadBalanceId = await getState()?.balanceUpload?.currentLoadBalance?.id;
      if (!currentLoadBalanceId) {
        return rejectWithValue({
          message: 'Kayıt bulunamadı.',
        });
      }
      const res = await balanceUploadDetailServices.loadBalanceDetailUpdate(body);
      const { detailTableFilter } = getState()?.balanceUploadDetail || {};
      await dispatch(getLoadBalanceDetailsGetPagedList(detailTableFilter));
      await dispatch(getById(currentLoadBalanceId));
      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const loadBalancePopUpMessage = createAsyncThunk(
  'loadBalancePopUpMessage',
  async (body, { getState, dispatch, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      const res = await balanceUploadDetailServices.loadBalancePopUpMessage({
        ...body,
        customerId,
      });
      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const postLoadBalancesReport = createAsyncThunk(
  'postLoadBalancesReport',
  async (body, { rejectWithValue }) => {
    try {
      const res = await balanceUploadDetailServices.postLoadBalancesReport({
        ...body,
      });
      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const postBalanceTransferReport = createAsyncThunk(
  'postBalanceTransferReport',
  async (body, { rejectWithValue }) => {
    try {
      const res = await balanceUploadDetailServices.postBalanceTransferReport({
        ...body,
      });
      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

const initialState = {
  selectedBalanceUploadDetails: [],
  detailTableFilter: {},
  allBalanceUploadCards: [],
  allCurrentEmployeeList: [],
  isFirstExcelUpload: false,
  balanceUploadDetails: [],
  latestDetailsRequestId: null,
  balanceFilterItems: [],
  balanceNotIncludedCards: [],
  excelUploadStatus: false,
  excelUploadErrorMessages: [],
  excelUploadErrorMessagesFreeze: [],
  excelUploadExcelErrorGroupMessages: [],
  excelUploadPrimaryColumnObject: null,
  tableProperty: {
    currentPage: 1,
    page: 1,
    pageSize: 10,
    totalCount: 0,
  },
  balanceTableProperty: {
    currentPage: 1,
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  },
  tablePropertyEmployeeList: {
    currentPage: 1,
    page: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  },
};
export const balanceUploadDetailSlice = createSlice({
  name: 'balanceUploadDetail',
  initialState,
  reducers: {
    setBalanceUploadDetails: (state, action) => {
      state.balanceUploadDetails = action.payload;
    },
    setBalanceFilterItems: (state, action) => {
      state.balanceFilterItems = action.payload;
    },
    resetBalanceFilterItems: (state, action) => {
      state.balanceFilterItems = [];
    },
    setBalanceUploadClear: (state, action) => {
      state.balanceUploadDetails = [];
      state.tableProperty = {
        currentPage: 1,
        page: 1,
        pageSize: 10,
        totalCount: 0,
      };
      state.excelUploadStatus = false;
    },
    setDetailTableFilter: (state, action) => {
      state.detailTableFilter = action.payload;
    },
    setDetailTableFilterClear: (state, action) => {
      state.detailTableFilter = {};
    },
    setSelectedBalanceUploadDetails: (state, action) => {
      state.selectedBalanceUploadDetails = action.payload;
    },
    setExcelUploadStatus: (state, action) => {
      state.excelUploadStatus = action.payload;
    },
    resetExcelUploadErrorMessages: (state, action) => {
      state.excelUploadErrorMessages = [];
      state.excelUploadErrorMessagesFreeze = [];
    },
    resetExcelUploadExcelErrorGroupMessages: (state, action) => {
      state.excelUploadExcelErrorGroupMessages = [];
    },
    excelUploadPrimaryColumn: (state, action) => {
      state.excelUploadPrimaryColumnObject = action.payload;
    },
    removeExcelUploadErrorMessages: (state, action) => {
      state.excelUploadErrorMessages = state.excelUploadErrorMessages.filter(
        (message) =>
          !(
            message.rowNumber === action.payload?.rowNumber &&
            message.column === action.payload?.column
          ),
      );
    },
    createExcelGroupErrors: (state, action) => {
      state.excelUploadExcelErrorGroupMessages = ExcelErrorMessageWithGroup(
        state.excelUploadErrorMessages,
      );
    },
    setBalanceNotIncludedCards: (state, action) => {
      state.balanceNotIncludedCards = action.payload;
    },
    resetCurrentLoadBalanceDetail: (state) => {
      //state.currentLoadBalance = {}
      return initialState;
    },
    setLoadBalancesReport: (state, action) => {
      state.balancesReportList = [];
      state.addressList = {};
      state.tableProperty = {
        currentPage: 1,
        page: 1,
        pageSize: 10,
        totalCount: 0,
      };
    },
    setBalanceTrasferReport: (state, action) => {
      state.balanceTrasferReportList = [];
      state.balanceTableProperty = {
        currentPage: 1,
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLoadBalanceDetailsGetPagedList.pending, (state, action) => {
      state.latestDetailsRequestId = action.meta.requestId;
    });
    // Ignore responses of outdated requests so they cannot overwrite a newer (e.g. filtered) list.
    builder.addCase(getLoadBalanceDetailsGetPagedList.fulfilled, (state, action) => {
      if (state.latestDetailsRequestId && action.meta.requestId !== state.latestDetailsRequestId) return;
      state.balanceUploadDetails = action?.payload?.data?.items || [];
      state.tableProperty = action?.payload?.data?.pagedProperty || {};
    });
    builder.addCase(getLoadBalanceDetailsGetPagedList.rejected, (state, action) => {
      if (state.latestDetailsRequestId && action.meta.requestId !== state.latestDetailsRequestId) return;
      state.balanceUploadDetails = [];
      state.tableProperty = {
        currentPage: 1,
        page: 1,
        pageSize: 10,
        totalCount: 0,
      };
    });
    builder.addCase(getAllLoadBalanceCards.fulfilled, (state, action) => {
      state.allBalanceUploadCards = action?.payload?.data?.items || [];
    });
    builder.addCase(getAllLoadBalanceCards.rejected, (state, action) => {
      state.allBalanceUploadCards = [];
    });
    builder.addCase(getCurrentEmployeeList.fulfilled, (state, action) => {
      state.allCurrentEmployeeList = action?.payload?.data?.items || [];
      state.tablePropertyEmployeeList.pageSize =
        action?.payload?.data?.pagedProperty?.pageSize || 0;
      state.tablePropertyEmployeeList.totalItems =
        action?.payload?.data?.pagedProperty?.totalItems || 0;
      state.tablePropertyEmployeeList.currentPage =
        action?.payload?.data?.pagedProperty?.currentPage || 1;
      state.tablePropertyEmployeeList.totalPages =
        action?.payload?.data?.pagedProperty?.totalPages || 1;
    });
    builder.addCase(getCurrentEmployeeList.rejected, (state, action) => {
      state.allCurrentEmployeeList = [];
      state.tablePropertyEmployeeList.pageSize = 10;
      state.tablePropertyEmployeeList.totalItems = 0;
      state.tablePropertyEmployeeList.currentPage = 1;
    });
    builder.addCase(loadBalanceDetailExcelUpload.fulfilled, (state, action) => {
      const response = action.payload.data;
      state.excelUploadStatus = true;
      state.balanceUploadDetails = response?.excelUploadData || [];
      state.isFirstExcelUpload = response?.isFirstExcelUpload || false;
      state.excelUploadErrorMessages = response?.validationMessages || [];
      state.excelUploadErrorMessagesFreeze = response?.validationMessages || [];
      if (response?.validationMessages?.length > 0) {
        state.excelUploadStatus = false;
      }
      state.excelUploadExcelErrorGroupMessages =
        ExcelErrorMessageWithGroup(response?.validationMessages) || [];
    });
    builder.addCase(loadBalanceDetailExcelUpload.rejected, (state, action) => {
      state.balanceUploadDetails = [];
      state.excelUploadStatus = false;
      state.excelUploadExcelErrorGroupMessages = [];
      state.excelUploadErrorMessages = [];
      state.excelUploadErrorMessagesFreeze = [];
      state.isFirstExcelUpload = false;
    });
    builder.addCase(postLoadBalancesReport.fulfilled, (state, action) => {
      state.balancesReportList = action?.payload?.data?.items || [];
      state.addressList = action?.payload?.data?.addressList || {};
      state.tableProperty.pageSize = action?.payload?.pageSize || 0;
      state.tableProperty.totalItems = action?.payload?.totalItems || 0;
      state.tableProperty.currentPage = action?.payload?.currentPage || 1;
    });
    builder.addCase(postLoadBalancesReport.rejected, (state, action) => {
      state.balancesReportList = [];
      state.addressList = {};
      state.tableProperty = {
        currentPage: 1,
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
      };
    });
    builder.addCase(postBalanceTransferReport.fulfilled, (state, action) => {
      const data = action?.payload?.data || {};
      state.balanceTrasferReportList = data.items || [];
      state.balanceTableProperty = {
        pageSize: action?.payload?.pageSize || state.balanceTableProperty.pageSize,
        totalItems: action?.payload?.totalItems || state.balanceTableProperty.totalItems,
        currentPage: action?.payload?.currentPage || state.balanceTableProperty.currentPage,
        totalPages: action?.payload?.totalPages || state.balanceTableProperty.totalPages,
      };
    });
    builder.addCase(postBalanceTransferReport.rejected, (state, action) => {
      state.balanceTrasferReportList = [];
      state.balanceTableProperty = {
        currentPage: 1,
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
      };
    });
  },
});

export const {
  setSelectedBalanceUploadDetails,
  setBalanceUploadDetails,
  setBalanceUploadClear,
  setDetailTableFilter,
  setExcelUploadStatus,
  setDetailTableFilterClear,
  resetCurrentLoadBalanceDetail,
  setBalanceFilterItems,
  resetBalanceFilterItems,
  removeExcelUploadErrorMessages,
  excelUploadPrimaryColumn,
  resetExcelUploadExcelErrorGroupMessages,
  createExcelGroupErrors,
  resetExcelUploadErrorMessages,
  setBalanceNotIncludedCards,
  setLoadBalancesReport,
  setBalanceTrasferReport,
} = balanceUploadDetailSlice.actions;
